import { useState, useEffect, useRef } from 'react';
import { BordOneToneIcon, NudosHoverText } from '../NudosComponents';
import NudosProductPills from '../NudosComponents/NudosProductPills/NudosProductPills';
import SerialInput from 'components/SerialInput/SerialInput';

import './MultipleSerialToolCard.scss';

export interface IToolData {
  id?: number;
  productId?: number | null;
  name?: string | null;
  pills?: { [key: string]: string | null };
  image?: string | null;
  brand?: string | null;
  brandImage?: string | null;
}

interface IMultipleSerialToolCardProps {
  tool: IToolData;
  quantity?: number;
  serials?: string[];
  customClassName?: string;
  duplicateSerialIndices?: Set<number>;
  onRemove?: () => void;
  onQuantityChange?: (quantity: number) => void;
  onSerialsChange?: (serials: string[]) => void;
  onSerialErrorChange?: (index: number, hasError: boolean) => void;
  onSerialValidatingChange?: (index: number, isValidating: boolean) => void;
}

/**
 * Tarjeta de herramienta con soporte para múltiples seriales y cantidad editable
 * @param tool - La herramienta a mostrar
 * @param quantity - La cantidad de la herramienta
 * @param serials - Los seriales de la herramienta
 * @param customClassName - Clase personalizada para el componente
 * @param onRemove - Función para remover la herramienta
 * @param onQuantityChange - Función para manejar el cambio de cantidad
 * @param onSerialsChange - Función para manejar el cambio de seriales
 * @param onSerialErrorChange - Función para manejar el cambio de error de serial
 * @param onSerialValidatingChange - Función para manejar el cambio de validación de serial
 */
const MultipleSerialToolCard = ({
  tool,
  quantity = 1,
  serials = [],
  customClassName = '',
  duplicateSerialIndices = new Set(),
  onRemove,
  onQuantityChange,
  onSerialsChange,
  onSerialErrorChange,
  onSerialValidatingChange
}: IMultipleSerialToolCardProps) => {
  const [localQuantity, setLocalQuantity] = useState<number | ''>(quantity);
  const onQuantityChangeRef = useRef(onQuantityChange);
  const onSerialsChangeRef = useRef(onSerialsChange);

  const characteristicsPills = (): JSX.Element | undefined => {
    if (!tool.pills || Object.keys(tool.pills)?.length === 0) return undefined;
    return <NudosProductPills pills={tool.pills} characterLimit={12} customHeight={20} xPadding="4px" yPadding="4px" />;
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setLocalQuantity('');
      return;
    }
    const newQuantity = Math.max(1, parseInt(value) || 1);
    setLocalQuantity(newQuantity);
    onQuantityChangeRef.current?.(newQuantity);
  };

  const handleQuantityBlur = () => {
    if (localQuantity === '' || localQuantity < 1) {
      setLocalQuantity(1);
      onQuantityChangeRef.current?.(1);
    }
  };

  const handleSerialChange = (index: number, serialValue: string) => {
    const newSerials = [...serials];
    newSerials[index] = serialValue;
    onSerialsChangeRef.current?.(newSerials);
  };

  const handleSerialInputChange = (index: number, serialValue: string) => {
    const newSerials = [...serials];
    newSerials[index] = serialValue;
    onSerialsChangeRef.current?.(newSerials);
  };

  const adjustSerialsToQuantity = () => {
    if (localQuantity === '' || serials.length === localQuantity) return;

    const newSerials = [...serials];
    while (newSerials.length < localQuantity) newSerials.push('');
    while (newSerials.length > localQuantity) newSerials.pop();

    onSerialsChangeRef.current?.(newSerials);
  };

  const getDuplicateError = (index: number): string | undefined => {
    return duplicateSerialIndices.has(index) ? 'Verifica que este serial no se repita' : undefined;
  };

  useEffect(() => {
    onQuantityChangeRef.current = onQuantityChange;
    onSerialsChangeRef.current = onSerialsChange;
  }, [onQuantityChange, onSerialsChange]);

  useEffect(() => {
    adjustSerialsToQuantity();
  }, [localQuantity]);

  useEffect(() => {
    if (quantity !== localQuantity) setLocalQuantity(quantity);
  }, [quantity]);

  return (
    <div className={`multipleSerialToolCard ${customClassName}`}>
      <button className="removeButton" onClick={onRemove} type="button">
        <BordOneToneIcon variant="x" customWidth="12px" stroke="white" />
      </button>

      <div className="cardContent">
        <div className="cardHeader">
          <div className="productInfo">
            <div className="imageContainer">
              {tool.image && <img src={tool.image} alt={tool.name || ''} className="imageCard" />}
            </div>

            <div className="productDetails">
              <div className="toolNameContainer">
                {tool.brandImage && <img src={tool.brandImage} alt={tool.brand || ''} className="iconCard" />}
                <NudosHoverText
                  customClassName="toolName"
                  text={tool.name || ''}
                  charactersLimit={40}
                  onlyIfTruncated
                />
              </div>
              <div className="toolDetailsContainer">{characteristicsPills()}</div>
            </div>
          </div>

          <div className="quantitySection">
            <label className="quantityLabel">Unidades</label>
            <input
              type="number"
              min={1}
              value={localQuantity}
              onChange={handleQuantityChange}
              onBlur={handleQuantityBlur}
              className="quantityInput"
            />
          </div>
        </div>

        <div className="serialsGrid">
          {Array.from({ length: localQuantity || 1 }).map((_, index) => (
            <SerialInput
              key={`serial-${tool.id}-${index}`}
              value={serials[index] || ''}
              onValueChange={serialValue => handleSerialChange(index, serialValue)}
              onInputChange={serialValue => handleSerialInputChange(index, serialValue)}
              onErrorChange={hasError => onSerialErrorChange?.(index, hasError)}
              onValidatingChange={isValidating => onSerialValidatingChange?.(index, isValidating)}
              externalError={getDuplicateError(index)}
              label="Serial*"
              customWidth="100%"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MultipleSerialToolCard;
